import { useNavigate } from 'react-router-dom'
import { Icon } from '@chakra-ui/icons'
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Center,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react'

import { isFreeRole } from '@/helpers/team'
import { useTeam } from '@/react-query/team'
import {
  useAcceptTeamInvite,
  useDeclineTeamInvite,
  useTeamInvite,
} from '@/react-query/team-invites'

export const JoinTeamModal = ({
  teamInviteId,
  isOpen,
  onClose,
}: {
  teamInviteId?: string
  isOpen: boolean
  onClose: () => void
}) => {
  if (!teamInviteId) {
    return null
  }

  const {
    data: teamInvite,
    isLoading,
    failureCount,
  } = useTeamInvite(teamInviteId)
  const navigate = useNavigate()
  const acceptTeamInvite = useAcceptTeamInvite(teamInviteId)

  const declineTeamInvite = useDeclineTeamInvite(teamInviteId)
  const team = useTeam()

  const handleClose = () => {
    navigate('/team')
    onClose()
  }

  const handleJoinTeam = async () => {
    try {
      await acceptTeamInvite.mutateAsync()
      navigate('/team')
      onClose()
    } catch (error) {
      console.error(error)
    }
  }

  const handleDeclineTeamInvite = async () => {
    try {
      await declineTeamInvite.mutateAsync()
      navigate('/team')
      onClose()
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Modal size={'5xl'} isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay bg="whiteAlpha.600" backdropFilter={'auto'} />
      <ModalContent
        bgColor={'#f5f5f5'}
        boxShadow={'0px 4px 30px 0px rgba(0, 0, 0, 0.25)'}
        rounded={'2.5rem'}
        maxW={'560px'}
        overflow={'hidden'}
        minH={failureCount ? 'auto' : '400px'}
        p={0}
      >
        {!failureCount && isLoading ? (
          <Center minH={'610px'}>
            <Spinner />
          </Center>
        ) : failureCount || !teamInvite ? (
          <>
            <ModalBody>
              <Text mt={6}>
                Sorry, looks like this invite is no longer active.
              </Text>
            </ModalBody>
            <ModalFooter justifyContent={'flex-start'}>
              <Button
                rounded={'1.25rem'}
                variant="brand-solid-white"
                onClick={handleClose}
              >
                Ok
              </Button>
            </ModalFooter>
          </>
        ) : team.data?.id ? (
          <>
            <ModalBody h={'200px'}>
              <Text mt={6}>Please leave the current team first.</Text>
            </ModalBody>
            <ModalFooter justifyContent={'flex-start'}>
              <Button
                rounded={'1.25rem'}
                variant="brand-solid-white"
                onClick={handleClose}
              >
                Ok
              </Button>
            </ModalFooter>
          </>
        ) : (
          <>
            <Box bg={'white'} borderTopRadius={'2.5rem'} p={3} px={6}>
              <ModalHeader>{`Join team "${teamInvite?.teamName}"`}</ModalHeader>
              <ModalCloseButton rounded={'full'} mt={5} mr={5} />
              <CurvedHireHireText />
              <AvatarGroup spacing="1rem" max={5} p={3}>
                {teamInvite?.teamMembers.map((teamMember) => (
                  <Avatar
                    src={teamMember.photoURL}
                    key={teamMember.displayName}
                    name={teamMember.displayName}
                    w={'3.5rem'}
                    h={'3.5rem'}
                  />
                ))}
              </AvatarGroup>
            </Box>
            <ModalBody p={6} px={10}>
              <Text mt={2}>
                {teamInvite.from?.displayName || ''} (
                <b>{teamInvite.from.email}</b>) has invited you
                <br />
                <br />
                👀 After joining, all your interviews will be visible to
                everyone. Similarly, you will see everyone else&apos;s
                interviews.
                <br />
                <br />
                {isFreeRole(teamInvite.role)
                  ? '👁️ This invite is for a view-only role. You can access all available interviews without making any changes.'
                  : '💰 Your subscription will be covered by the team admin.'}
              </Text>
            </ModalBody>
            <ModalFooter justifyContent={'flex-start'} pb={8} px={10}>
              <Button
                type="submit"
                variant={'brand-solid'}
                isLoading={acceptTeamInvite.isPending}
                onClick={handleJoinTeam}
                mr={3}
                rounded={'1.25rem'}
              >
                Accept invitation
              </Button>
              <Button
                type="submit"
                variant="brand-solid-white"
                isLoading={declineTeamInvite.isPending}
                onClick={handleDeclineTeamInvite}
                mr={3}
                rounded={'1.25rem'}
              >
                Decline
              </Button>
              <Button
                rounded={'1.25rem'}
                variant="brand-solid-white"
                onClick={handleClose}
              >
                Not now
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

const CurvedHireHireText = () => {
  return (
    <Icon
      viewBox="0 0 600 100"
      height={'150px'}
      width={'600px'}
      ml={'-6'}
      mt={'-6'}
      color={'gray.400'}
      overflow={'hidden'}
      display={'block'}
    >
      <path
        className="select-none text-gray-400"
        id="curve2"
        fill="transparent"
        d="M3,62 C101,130 310,110 598,-10"
      />
      <text>
        <textPath
          href="#curve2"
          className="select-none text-2xl text-gray-400"
          style={{
            fontWeight: 600,
          }}
          fill={'rgb(176,176,176)'}
        >
          hirehire hirehire hirehire hirehire hirehire hirehire hirehire
        </textPath>
      </text>
    </Icon>
  )
}
